// src/pages/admin/menu/AdminNav.tsx

import { useState, useCallback, useEffect, useRef } from "react";
import { getApiUrl, fetchWithAuth } from '../../../api'

interface PageOption {
  id: number;
  slug: string;
  title: string;
}

interface BoardOption {
  id: number;
  slug: string;
  name: string;
}

interface NavAdminItem {
  id: number;
  label: string;
  href: string | null;
  page_id: number | null;
  parent_id: number | null;
  sort_order: number;
  is_visible: number;
  menu_type: string;
  children: NavAdminItem[];
}

interface EditForm {
  label: string;
  type: "page" | "board" | "none";
  page_id: number | null;
  href: string;
  is_visible: number;
  board_slug: string;
}

// Flat item used during drag
interface FlatItem {
  id: number;
  parentId: number | null;
  depth: number; // 0 = top, 1 = child
  item: NavAdminItem;
}

const TYPE_LABEL: Record<string, { label: string; className: string }> = {
  dropdown: { label: "드롭다운", className: "bg-green-100 text-green-700" },
  default:  { label: "default",  className: "bg-gray-100 text-gray-400" },
  page:     { label: "페이지",    className: "bg-[#f5f2f0] text-[#887267]" },
  board:    { label: "게시판",    className: "bg-blue-50 text-blue-600" },
  apply:    { label: "신청",      className: "bg-orange-50 text-orange-500" },
};

function TypeBadge({ item }: { item: NavAdminItem }) {
  const { label, className } = TYPE_LABEL[item.menu_type] ?? TYPE_LABEL.dropdown;
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${className}`}>
      {label}
    </span>
  );
}

/** Flatten tree into ordered list */
function flattenTree(tree: NavAdminItem[]): FlatItem[] {
  const result: FlatItem[] = [];
  for (const item of tree) {
    result.push({ id: item.id, parentId: null, depth: 0, item });
    for (const child of item.children) {
      result.push({ id: child.id, parentId: item.id, depth: 1, item: child });
    }
  }
  return result;
}

/** Rebuild tree from flat list */
function rebuildTree(flat: FlatItem[]): NavAdminItem[] {
  const tops: NavAdminItem[] = [];
  for (const fi of flat) {
    if (fi.depth === 0) {
      tops.push({ ...fi.item, children: [], parent_id: null });
    }
  }
  for (const fi of flat) {
    if (fi.depth === 1 && fi.parentId !== null) {
      const parent = tops.find(t => t.id === fi.parentId);
      if (parent) {
        parent.children.push({ ...fi.item, children: [], parent_id: fi.parentId });
      }
    }
  }
  return tops;
}

export default function AdminNav() {
  const [tree, setTree] = useState<NavAdminItem[]>([]);
  const [pages, setPages] = useState<PageOption[]>([]);
  const [boards, setBoards] = useState<BoardOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // ── drag state ────────────────────────────────────────────────────────────
  const [flatItems, setFlatItems] = useState<FlatItem[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  // indent shift applied to dragged item: -1 = promote to top, 0 = keep, 1 = demote to child
  const [indentShift, setIndentShift] = useState<number>(0);
  const dragStartX = useRef<number>(0);
  const dragCurrentX = useRef<number>(0);
  const INDENT_PX = 48; // px per level

  useEffect(() => {
    setFlatItems(flattenTree(tree));
  }, [tree]);

  // ── toast ────────────────────────────────────────────────────────────────
  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  };

  // ── fetch ────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [navRes, pageRes, boardRes] = await Promise.all([
        fetchWithAuth(getApiUrl('/marubit/api/nav/admin')),
        fetchWithAuth(getApiUrl('/marubit/api/page')),
        fetchWithAuth(getApiUrl('/marubit/api/board')),
      ]);
      if (navRes.ok) setTree(await navRes.json());
      if (pageRes.ok) setPages(await pageRes.json());
      if (boardRes.ok) setBoards(await boardRes.json());
    } catch {
      showToast("데이터를 불러오지 못했습니다.", false);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── drag handlers ─────────────────────────────────────────────────────────
  const handleDragStart = (e: React.DragEvent, idx: number) => {
    setDragIndex(idx);
    setIndentShift(0);
    dragStartX.current = e.clientX;
    dragCurrentX.current = e.clientX;
    e.dataTransfer.effectAllowed = "move";
    // ghost image: slightly transparent
    const el = e.currentTarget as HTMLElement;
    e.dataTransfer.setDragImage(el, el.offsetWidth / 2, 20);
  };

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    dragCurrentX.current = e.clientX;

    const dx = dragCurrentX.current - dragStartX.current;
    // each INDENT_PX of horizontal movement = +/- 1 indent level
    const shift = Math.round(dx / INDENT_PX);
    setIndentShift(shift);
    setDragOverIndex(idx);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIndex === null || dragOverIndex === null) {
      setDragIndex(null); setDragOverIndex(null); setIndentShift(0);
      return;
    }

    const newFlat = [...flatItems];
    const dragged = newFlat[dragIndex];

    // remove from current position
    newFlat.splice(dragIndex, 1);

    // compute target index after removal
    const targetIdx = dragOverIndex > dragIndex ? dragOverIndex - 1 : dragOverIndex;
    newFlat.splice(targetIdx, 0, dragged);

    // determine new depth & parent
    const draggedDepthBefore = dragged.depth;
    let newDepth = draggedDepthBefore + indentShift;
    newDepth = Math.max(0, Math.min(1, newDepth)); // clamp 0–1

    // find new parent: look at the item immediately above in flat list
    let newParentId: number | null = null;
    if (newDepth === 1) {
      // scan upward for the nearest depth-0 item
      for (let i = targetIdx - 1; i >= 0; i--) {
        if (newFlat[i].depth === 0) {
          newParentId = newFlat[i].id;
          break;
        }
      }
      // if none found above, keep as top level
      if (newParentId === null) newDepth = 0;
    }

    // update the dragged item in place
    newFlat[targetIdx] = { ...dragged, depth: newDepth, parentId: newParentId };

    // also move dragged item's children (only relevant if depth changed to 0→1, keep together)
    // for simplicity, just reorder; children stay where they were (same as WP behavior)

    setFlatItems(newFlat);
    setDragIndex(null);
    setDragOverIndex(null);
    setIndentShift(0);

    // rebuild and persist
    const newTree = rebuildTree(newFlat);
    setTree(newTree);
    await persistReorder(newFlat);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
    setIndentShift(0);
  };

  const persistReorder = async (flat: FlatItem[]) => {
    try {
      const items = flat.map((fi, i) => ({
        id: fi.id,
        parent_id: fi.parentId,
        sort_order: i,
      }));
      await fetchWithAuth(getApiUrl('/marubit/api/nav/admin/reorder'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
    } catch {
      showToast("순서 변경 실패", false);
    }
  };

  // ── select / edit ────────────────────────────────────────────────────────
  const selectItem = (item: NavAdminItem) => {
    setSelectedId(item.id);
    let type: "page" | "board" | "none" = "none";
    if (item.page_id) type = "page";
    else if (item.menu_type === "apply" || item.menu_type === "board") type = "board";
    else if (item.href) type = "board";

    let boardSlug = "";
    if (item.href?.startsWith("/board/")) boardSlug = item.href.replace("/board/", "");
    else if (item.href) boardSlug = item.href;

    setForm({ label: item.label, type, page_id: item.page_id, href: item.href || "", is_visible: item.is_visible, board_slug: boardSlug });
  };

  const findItem = (id: number): NavAdminItem | null => {
    for (const m of tree) {
      if (m.id === id) return m;
      for (const c of m.children) if (c.id === id) return c;
    }
    return null;
  };

  const saveEdit = async () => {
    if (!form || selectedId === null) return;
    setSaving(true);
    try {
      let href = null;
      if (form.type === "board" && form.board_slug) href = `/board/${form.board_slug}`;

      let menu_type: string | undefined;
      if (!isApply && selectedItem?.menu_type !== "default") {
        if (form.type === "board") menu_type = "board";
        else if (form.type === "page") menu_type = "page";
        else menu_type = "dropdown";
      }

      const res = await fetchWithAuth(getApiUrl(`/marubit/api/nav/admin/${selectedId}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          label: form.label,
          href,
          page_id: form.type === "page" ? form.page_id : null,
          is_visible: form.is_visible,
          ...(menu_type !== undefined && { menu_type }),
        }),
      });
      if (res.ok) { showToast("저장되었습니다."); fetchAll(); setSelectedId(null); setForm(null); }
      else showToast("저장 실패", false);
    } catch { showToast("오류가 발생했습니다.", false); }
    setSaving(false);
  };

  const addTopMenu = async () => {
    setSaving(true);
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/nav/admin'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label: "새 메뉴", href: null, page_id: null, parent_id: null, sort_order: tree.length, is_visible: 1 }),
      });
      if (res.ok) { showToast("메뉴가 추가되었습니다."); fetchAll(); }
      else showToast("추가 실패", false);
    } catch { showToast("오류가 발생했습니다.", false); }
    setSaving(false);
  };

  const addChildMenu = async (parentId: number, parentChildren: NavAdminItem[]) => {
    setSaving(true);
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/nav/admin'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label: "새 하위 메뉴", href: null, page_id: null, parent_id: parentId, sort_order: parentChildren.length, is_visible: 1 }),
      });
      if (res.ok) { showToast("하위 메뉴가 추가되었습니다."); fetchAll(); }
      else showToast("추가 실패", false);
    } catch { showToast("오류가 발생했습니다.", false); }
    setSaving(false);
  };

  const deleteItem = async (id: number) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    setSaving(true);
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/nav/admin/${id}`), { method: "DELETE" });
      if (res.ok) {
        showToast("삭제되었습니다.");
        if (selectedId === id) { setSelectedId(null); setForm(null); }
        fetchAll();
      } else {
        const err = await res.json().catch(() => null);
        showToast(err?.detail ?? "삭제 실패", false);
      }
    } catch { showToast("오류가 발생했습니다.", false); }
    setSaving(false);
  };

  const moveItem = async (id: number, direction: "up" | "down", parentId: number | null) => {
    const siblings = parentId === null ? tree : tree.find((m) => m.id === parentId)?.children ?? [];
    const idx = siblings.findIndex((s) => s.id === id);
    if (direction === "up" && idx === 0) return;
    if (direction === "down" && idx === siblings.length - 1) return;
    const newSiblings = [...siblings];
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    [newSiblings[idx], newSiblings[swapIdx]] = [newSiblings[swapIdx], newSiblings[idx]];
    try {
      await fetchWithAuth(getApiUrl('/marubit/api/nav/admin/reorder'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: newSiblings.map((s, i) => ({ id: s.id, parent_id: parentId, sort_order: i })) }),
      });
      fetchAll();
    } catch { showToast("순서 변경 실패", false); }
  };

  if (loading) {
    return (
      <div className="bg-[#f5f2f0] min-h-screen p-8">
        <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-6">메뉴 관리</h1>
        <div className="flex items-center justify-center py-16 text-gray-300 text-sm">불러오는 중...</div>
      </div>
    );
  }

  const selectedItem = selectedId ? findItem(selectedId) : null;
  const isLevel1 = selectedItem?.parent_id === null;
  const isApply = selectedItem?.menu_type === "apply";
  const onCancel = () => { setSelectedId(null); setForm(null); };

  const editFormJsx = !form ? null : (
    <>
      {(isApply || selectedItem?.menu_type === "default") && (
        <div className="mb-4 px-3 py-2.5 bg-orange-50 border border-orange-100 rounded-lg text-[12px] text-orange-500 leading-relaxed">
          ⚠️ 이 메뉴는 시스템 고정 항목으로 메뉴이름, 노출상태 이외의 직접 수정이 제한됩니다.<br />
          변경이 필요하시면 <span className="font-semibold">하나네트웍스</span>로 문의해 주세요.
        </div>
      )}
      <div className="mb-4">
        <label className="block text-[12px] text-gray-400 mb-1">메뉴 이름</label>
        <input
          className="w-full px-3 py-2 text-[13px] border border-[#e8e0dc] rounded-lg outline-none text-[#2a1f1a] focus:border-[#887267]"
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
        />
      </div>
      {(isApply || selectedItem?.menu_type === "default") && (
        <div className="mb-4">
          <label className="block text-[12px] text-gray-400 mb-1">링크</label>
          <input
            className="w-full px-3 py-2 text-[13px] border border-[#e8e0dc] rounded-lg bg-gray-50 text-gray-400 cursor-not-allowed"
            value={form.href || "/program"}
            readOnly
          />
        </div>
      )}
      {!isApply && selectedItem?.menu_type !== "default" && (
        <>
          <div className="mb-4">
            <label className="block text-[12px] text-gray-400 mb-1">연결 유형</label>
            {(["page", "board", "none"] as const).map((t) => (
              <div
                key={t}
                className={`flex items-center gap-2 px-3 py-2 mb-1.5 rounded-lg border cursor-pointer text-[13px] transition-colors ${
                  form.type === t ? "border-[#887267] bg-[#f5f2f0] text-[#887267]" : "border-[#e8e0dc] bg-white text-gray-600"
                }`}
                onClick={() => setForm({ ...form, type: t, page_id: null, href: "", board_slug: "" })}
              >
                <span className="w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0" style={{ borderColor: form.type === t ? "#887267" : "#ccc" }}>
                  {form.type === t && <span className="w-1.5 h-1.5 rounded-full bg-[#887267] block" />}
                </span>
                {t === "page" ? "페이지 선택" : t === "board" ? "게시판 선택" : "링크 없음 (드롭다운만)"}
              </div>
            ))}
          </div>
          {!isLevel1 && form.type === "page" && (
            <div className="mb-4">
              <label className="block text-[12px] text-gray-400 mb-1">페이지</label>
              <select
                className="w-full px-3 py-2 text-[13px] border border-[#e8e0dc] rounded-lg outline-none text-[#2a1f1a] focus:border-[#887267] bg-white"
                value={form.page_id ?? ""}
                onChange={(e) => setForm({ ...form, page_id: e.target.value ? Number(e.target.value) : null })}
              >
                <option value="">선택하세요</option>
                {pages.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
          )}
          {form.type === "board" && (
            <div className="mb-4">
              <label className="block text-[12px] text-gray-400 mb-1">게시판</label>
              <select
                className="w-full px-3 py-2 text-[13px] border border-[#e8e0dc] rounded-lg outline-none text-[#2a1f1a] focus:border-[#887267] bg-white"
                value={form.board_slug}
                onChange={(e) => setForm({ ...form, board_slug: e.target.value, href: e.target.value ? `/board/${e.target.value}` : "" })}
              >
                <option value="">선택하세요</option>
                {boards.map((b) => <option key={b.id} value={b.slug}>{b.name}</option>)}
              </select>
            </div>
          )}
        </>
      )}
      <div className="flex items-center justify-between py-3 border-t border-[#e8e0dc] mb-4">
        <span className="text-[13px] text-gray-600">메뉴 노출</span>
        <div
          className="w-10 h-[22px] rounded-full relative cursor-pointer transition-colors flex-shrink-0"
          style={{ background: form.is_visible ? "#887267" : "#ddd" }}
          onClick={() => setForm({ ...form, is_visible: form.is_visible ? 0 : 1 })}
        >
          <div className="w-[18px] h-[18px] rounded-full bg-white absolute top-0.5 transition-all" style={{ left: form.is_visible ? 20 : 2 }} />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button className="text-[13px] text-red-400 border border-red-100 rounded-lg px-3 py-1.5 bg-transparent cursor-pointer" onClick={onCancel}>취소</button>
        <button className="bg-[#887267] text-white text-[13px] font-semibold px-4 py-1.5 rounded-lg border-none cursor-pointer disabled:opacity-50" onClick={saveEdit} disabled={saving}>
          {saving ? "저장 중..." : "저장"}
        </button>
      </div>
    </>
  );

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-8">

      {toast && (
        <div className={`fixed top-5 right-5 z-50 px-4 py-2.5 rounded-xl text-white text-[13px] font-semibold ${toast.ok ? "bg-[#887267]" : "bg-red-500"}`}>
          {toast.msg}
        </div>
      )}

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">메뉴 관리</h1>
          <p className="text-[13px] text-gray-400">
            {new Date().toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric", weekday: "long" })}
          </p>
        </div>
        <button
          className="bg-[#887267] text-white text-[13px] font-semibold px-4 py-2 rounded-lg border-none cursor-pointer disabled:opacity-50"
          onClick={addTopMenu}
          disabled={saving}
        >
          + 최상위 메뉴 추가
        </button>
      </div>

      {/* 모바일: 바텀시트 모달 */}
      {form && (
        <div className="xl:hidden fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl w-full max-w-[480px] max-h-[85vh] overflow-y-auto shadow-[0_8px_32px_rgba(0,0,0,0.2)]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#e8e0dc] sticky top-0 bg-white">
              <span className="text-[15px] font-bold text-[#2a1f1a]">메뉴 편집</span>
              <button
                onClick={() => { setSelectedId(null); setForm(null); }}
                className="bg-transparent border-none cursor-pointer text-gray-400 text-[20px] leading-none"
              >✕</button>
            </div>
            <div className="p-5">
              {editFormJsx}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[4fr_3fr] gap-4 items-start">

        {/* ── 트리 (드래그앤드롭) ─────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#e8e0dc]">
            <div>
              <span className="text-[14px] font-bold text-[#2a1f1a]">메뉴 구조</span>
              <span className="ml-2 text-[11px] text-gray-400">드래그로 순서·계층 변경 (좌우 이동으로 들여쓰기 조절)</span>
            </div>
            <button className="text-[12px] text-[#887267] border border-[#e8e0dc] rounded-md px-2 py-1 bg-transparent cursor-pointer" onClick={fetchAll}>
              ↺ 새로고침
            </button>
          </div>

          {flatItems.length === 0 ? (
            <p className="text-center text-gray-300 text-[13px] py-10">메뉴가 없습니다.</p>
          ) : (
            <div className="py-1">
              {flatItems.map((fi, idx) => {
                const item = fi.item;
                const isDragging = dragIndex === idx;
                const isOver = dragOverIndex === idx;
                // preview indent while dragging this row
                const previewDepth = isDragging
                  ? Math.max(0, Math.min(1, fi.depth + indentShift))
                  : fi.depth;

                // find parent id for move buttons
                const parentId = fi.parentId;

                // siblings for disable check
                const siblings = parentId === null
                  ? flatItems.filter(f => f.depth === 0)
                  : flatItems.filter(f => f.depth === 1 && f.parentId === parentId);
                const sibIdx = siblings.findIndex(s => s.id === fi.id);

                return (
                  <div
                    key={item.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-2 px-4 py-2.5 cursor-grab active:cursor-grabbing transition-all border-l-[3px] select-none
                      ${selectedId === item.id ? "bg-[#f5f2f0] border-[#887267]" : "border-transparent hover:bg-[#fdf8f0]"}
                      ${isDragging ? "opacity-40" : ""}
                      ${isOver && !isDragging ? "border-t-2 border-t-[#887267]" : ""}
                    `}
                    style={{ paddingLeft: `${16 + previewDepth * 44}px` }}
                    onClick={() => selectItem(item)}
                  >
                    {/* drag handle */}
                    <span className="text-gray-300 text-[13px] min-w-[14px] cursor-grab">⠿</span>

                    {/* indent indicator */}
                    {previewDepth > 0 && (
                      <span className="text-gray-200 text-[11px]">└</span>
                    )}

                    <span className={`text-[13px] font-medium flex-1 text-[#2a1f1a] ${item.is_visible === 0 ? "opacity-40" : ""}`}>
                      {item.label}
                    </span>

                    <TypeBadge item={item} />
                    {item.is_visible === 0 && <span className="text-[11px] text-gray-300">숨김</span>}

                    {/* arrow buttons */}
                    <button
                      className="text-[12px] text-[#887267] border border-[#e8e0dc] rounded px-1.5 py-0.5 bg-transparent cursor-pointer disabled:opacity-30"
                      onClick={(e) => { e.stopPropagation(); moveItem(item.id, "up", parentId); }}
                      disabled={sibIdx === 0}
                    >↑</button>
                    <button
                      className="text-[12px] text-[#887267] border border-[#e8e0dc] rounded px-1.5 py-0.5 bg-transparent cursor-pointer disabled:opacity-30"
                      onClick={(e) => { e.stopPropagation(); moveItem(item.id, "down", parentId); }}
                      disabled={sibIdx === siblings.length - 1}
                    >↓</button>

                    {/* top-level only: add child */}
                    {fi.depth === 0 && (
                      <button
                        className="text-[12px] text-[#887267] border border-[#e8e0dc] rounded px-1.5 py-0.5 bg-transparent cursor-pointer"
                        onClick={(e) => { e.stopPropagation(); addChildMenu(item.id, item.children); }}
                      >+ 하위</button>
                    )}

                    <button
                      className="text-[12px] text-red-400 bg-transparent border-none cursor-pointer px-1"
                      onClick={(e) => { e.stopPropagation(); deleteItem(item.id); }}
                    >✕</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── 편집 패널 (데스크탑 전용) ────────────────────────────────── */}
        <div className="hidden xl:block bg-white rounded-2xl border border-[#e8e0dc] p-5">
          {!editFormJsx
            ? <p className="text-center text-gray-300 text-[13px] py-8">왼쪽에서 메뉴 항목을 선택하세요</p>
            : editFormJsx
          }
        </div>

      </div>
    </div>
  );
}